import { useMemo, useState } from 'react';
import { Check, Loader2, Save, X } from 'lucide-react';
import {
  useMobileConfigCatalogue,
  useProductsPosConfig,
  useUpdateMobileConfig,
  type MobileConfig,
  type MobilePageRule,
  type MobileRole,
} from '@/hooks/use-pos-config';
import { useLocationStore } from '@/stores/location-store';
import { toast } from '@/stores/toast-store';
import { cn } from '@/lib/utils';

/**
 * Mobile Config — which roles may open which page of the SuperNova mobile app,
 * and which may see restricted fields, per location.
 *
 * A role matrix rather than a "minimum role and above" dropdown, matching
 * Reports → POS → Settings: a floor cannot express "manager but not admin",
 * and operators already read this shape elsewhere in the dashboard.
 *
 * The page list comes from the products service's catalogue rather than being
 * hardcoded here — the app needs a route compiled in for every page, so the
 * editor must not be able to offer one the build lacks, and a page added to
 * the app appears here without a dashboard release.
 *
 * Only overrides are stored. A page nobody has touched stays absent from the
 * config and inherits the build's default, so an untouched location behaves
 * exactly like today's app.
 */

const ROLE_LABELS: Record<MobileRole, string> = {
  super_admin: 'Super Admin',
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  employee: 'Employee',
};

/** Friendly names for the pages the app ships today. Unknown keys fall back to
 *  a humanised form of the key itself, so a page added to the app is readable
 *  here before anyone updates this map. */
const PAGE_LABELS: Record<string, string> = {
  home: 'Home',
  find_product: 'Find Product',
  stock_in: 'Stock In',
  stock_out: 'Stock Out',
  stock_count: 'Stock Count',
  new_product: 'New Product',
  print_labels: 'Print Labels',
  batch_labels: 'Batch Labels',
  purchase_orders: 'Purchase Orders',
  dashboard: 'Dashboard',
  product_intelligence: 'Product Intelligence',
  settings: 'Settings',
};

const FIELD_LABELS: Record<string, string> = {
  costPrice: 'Cost price',
  sellingPrice: 'Selling price',
  vendorPrice: 'Vendor price',
};

const FIELD_HINTS: Record<string, string> = {
  costPrice: 'Product cost and purchase-order costs.',
  sellingPrice: 'Retail price on product details and label previews.',
  vendorPrice: "What each vendor charges, on Find Product → Vendors. Also orders that tab's rows cheapest-first.",
};

/** The mobile Details-tab sections, in the order the app shows them. */
const EDIT_SECTION_LABELS: Record<string, string> = {
  basicInfo: 'Basic Info',
  unitsOfMeasure: 'Units of Measure',
  casePackPallet: 'Selling Price as Case / Pack / Pallet',
  storeInventory: 'Store Inventory',
  department: 'Department',
  productOptions: 'Product Options',
  productPlacement: 'Product Placement',
  customFields: 'Custom Fields',
};

const EDIT_SECTION_HINTS: Record<string, string> = {
  basicInfo: 'Name, display name, barcodes and SKU.',
  unitsOfMeasure: "Selling and purchasing unit, and this store's price and cost.",
  casePackPallet: 'Add, change or remove case, pack and pallet units and their prices.',
  storeInventory:
    'Price, stock and reorder settings for the store the user is signed in to. Other stores are edited by switching to them.',
  department: 'Department, categories and modifier groups.',
  productOptions: 'Taxable, EBT, weight, age check, kitchen print and the other POS toggles.',
  productPlacement: 'Building, aisle, bin, POS sort order, and expiry dates with batch numbers.',
  customFields: 'Ingredients notes, original price and the custom field.',
};

/** The app's default for a field with no per-location override. Cost follows
 *  the historic cost-visible list, which POS Config → Cost Visibility also
 *  drives; vendor price adds managers, who negotiate it; selling price is
 *  open. Used only to render the unconfigured state — the server is the
 *  authority when permissions are actually resolved. */
function defaultFieldRoles(field: string, roles: MobileRole[]): MobileRole[] {
  if (field === 'costPrice') return roles.filter((r) => r === 'owner' || r === 'admin');
  if (field === 'vendorPrice') return roles.filter((r) => r === 'owner' || r === 'admin' || r === 'manager');
  return [...roles];
}

function humanise(key: string): string {
  return key
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/** A matrix cell. Green check = allowed, red X = not — the same affordance as
 *  the report-visibility matrix, so the two read the same way. */
function ToggleCell({
  on,
  label,
  onClick,
  disabled = false,
}: {
  on: boolean;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <td className="px-3 py-2 text-center">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        aria-pressed={on}
        className={cn(
          'inline-flex h-7 w-7 items-center justify-center rounded-md border transition-colors',
          on
            ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100'
            : 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100',
          disabled && 'cursor-not-allowed opacity-40 hover:bg-transparent',
        )}
      >
        {on ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
      </button>
    </td>
  );
}

export function MobileConfigPage() {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const catalogueQuery = useMobileConfigCatalogue();
  const configQuery = useProductsPosConfig(selectedLocationId);
  const update = useUpdateMobileConfig();

  // `null` while untouched so the form tracks the server value as it refetches;
  // the first edit forks it into local state.
  const [draft, setDraft] = useState<MobileConfig | null>(null);

  const saved: MobileConfig = useMemo(() => configQuery.data?.mobileConfig ?? {}, [configQuery.data]);
  const value = draft ?? saved;
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(saved);

  const roles = catalogueQuery.data?.roles ?? [];
  const pages = catalogueQuery.data?.pages ?? [];
  const fields = catalogueQuery.data?.fields ?? [];
  const editSections = catalogueQuery.data?.editSections ?? [];

  const edit = (fn: (cur: MobileConfig) => MobileConfig) => setDraft((cur) => fn(cur ?? saved));

  /** Roles allowed on a page right now: the stored list, or the build default.
   *  A `null` default means the page ships open, which shows as all checked. */
  const pageRoles = (key: string, defaultRoles: MobileRole[] | null): MobileRole[] => {
    const stored = value.pages?.[key]?.roles;
    if (stored) return stored;
    return defaultRoles === null ? [...roles] : defaultRoles;
  };

  const setPageRule = (key: string, rule: MobilePageRule) => {
    edit((cur) => {
      const next: MobileConfig = { ...cur };
      const nextPages = { ...(next.pages ?? {}) };
      // An empty rule is indistinguishable from "not configured" — drop the key
      // so the stored config stays the set of real overrides.
      if (rule.roles === undefined && rule.enabled === undefined) delete nextPages[key];
      else nextPages[key] = rule;
      if (Object.keys(nextPages).length) next.pages = nextPages;
      else delete next.pages;
      return next;
    });
  };

  const togglePageRole = (key: string, defaultRoles: MobileRole[] | null, role: MobileRole) => {
    const current = pageRoles(key, defaultRoles);
    const nextRoles = current.includes(role) ? current.filter((r) => r !== role) : [...current, role];
    setPageRule(key, { ...(value.pages?.[key] ?? {}), roles: nextRoles });
  };

  const resetPage = (key: string) => setPageRule(key, { ...(value.pages?.[key] ?? {}), roles: undefined });

  const fieldRoles = (field: string): MobileRole[] => value.fields?.[field] ?? defaultFieldRoles(field, roles);

  const toggleFieldRole = (field: string, role: MobileRole) => {
    const current = fieldRoles(field);
    const next = current.includes(role) ? current.filter((r) => r !== role) : [...current, role];
    edit((cur) => ({ ...cur, fields: { ...(cur.fields ?? {}), [field]: next } }));
  };

  const resetField = (field: string) => {
    edit((cur) => {
      const next: MobileConfig = { ...cur };
      const nextFields = { ...(next.fields ?? {}) };
      delete nextFields[field];
      if (Object.keys(nextFields).length) next.fields = nextFields;
      else delete next.fields;
      return next;
    });
  };

  const sectionRoles = (key: string, defaultRoles: MobileRole[]): MobileRole[] => value.edit?.[key] ?? defaultRoles;

  const toggleSectionRole = (key: string, defaultRoles: MobileRole[], role: MobileRole) => {
    const current = sectionRoles(key, defaultRoles);
    const next = current.includes(role) ? current.filter((r) => r !== role) : [...current, role];
    edit((cur) => ({ ...cur, edit: { ...(cur.edit ?? {}), [key]: next } }));
  };

  const resetSection = (key: string) => {
    edit((cur) => {
      const next: MobileConfig = { ...cur };
      const nextEdit = { ...(next.edit ?? {}) };
      delete nextEdit[key];
      if (Object.keys(nextEdit).length) next.edit = nextEdit;
      else delete next.edit;
      return next;
    });
  };

  /** Allow or deny every role on every page at once. */
  const bulkSet = (allow: boolean) => {
    edit((cur) => {
      const nextPages = { ...(cur.pages ?? {}) };
      for (const { key } of pages) {
        nextPages[key] = { ...(nextPages[key] ?? {}), roles: allow ? [...roles] : [] };
      }
      return { ...cur, pages: nextPages };
    });
  };

  const save = async () => {
    if (!selectedLocationId || !draft) return;
    try {
      await update.mutateAsync({ locationId: selectedLocationId, mobileConfig: draft });
      setDraft(null);
      toast.success(`Mobile permissions saved for ${selectedLocationName || 'this location'}`);
    } catch (err) {
      const message = (err as { error?: { message?: string } })?.error?.message;
      toast.error(message || 'Failed to save mobile permissions');
    }
  };

  if (!selectedLocationId) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-white p-12 text-center text-sm text-muted-foreground">
        Select a location to configure its mobile app.
      </div>
    );
  }
  if (catalogueQuery.isLoading || configQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading mobile configuration…
      </div>
    );
  }
  if (catalogueQuery.isError) {
    return (
      <p className="text-sm text-red-600">
        Could not reach the products service, so the page list is unavailable. Nothing has been changed.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-foreground">Mobile App Permissions</h3>
        <p className="text-xs text-muted-foreground">
          Control which roles can open each page of the mobile app
          {selectedLocationName ? ` at ${selectedLocationName}` : ''}. Uncheck a role to hide a page from it, or switch
          a page off to hide it from everyone. Super Admin always has full access.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => bulkSet(true)}
            className="h-9 rounded-lg border border-border px-3 text-xs font-medium text-foreground hover:bg-muted"
          >
            Allow all
          </button>
          <button
            type="button"
            onClick={() => bulkSet(false)}
            className="h-9 rounded-lg border border-border px-3 text-xs font-medium text-foreground hover:bg-muted"
          >
            Deny all
          </button>
        </div>
      </div>

      {/* Page matrix */}
      <div className="overflow-x-auto rounded-lg border border-border bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">Page</th>
              {roles.map((role) => (
                <th key={role} className="px-3 py-2.5 text-center text-xs font-medium text-muted-foreground">
                  {ROLE_LABELS[role]}
                </th>
              ))}
              <th className="px-3 py-2.5 text-center text-xs font-medium text-muted-foreground">Available</th>
              <th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {pages.map(({ key, defaultRoles }) => {
              const rule = value.pages?.[key];
              const off = rule?.enabled === false;
              const allowed = pageRoles(key, defaultRoles);
              const title = PAGE_LABELS[key] ?? humanise(key);
              return (
                <tr key={key} className="border-b border-border last:border-0 hover:bg-muted/30">
                  <td className="px-3 py-2 font-medium text-foreground">{title}</td>
                  {roles.map((role) => {
                    const on = !off && allowed.includes(role);
                    return (
                      <ToggleCell
                        key={role}
                        on={on}
                        disabled={off}
                        label={`${on ? 'Hide' : 'Show'} ${title} for ${ROLE_LABELS[role]}`}
                        onClick={() => togglePageRole(key, defaultRoles, role)}
                      />
                    );
                  })}
                  {/* Switching a page off hides it from every role at once,
                      which is a different statement from unchecking each one. */}
                  <ToggleCell
                    on={!off}
                    label={`${off ? 'Enable' : 'Disable'} ${title} at this location`}
                    onClick={() => setPageRule(key, { ...(rule ?? {}), enabled: off ? undefined : false })}
                  />
                  <td className="px-3 py-2 text-right">
                    {rule?.roles ? (
                      <button
                        type="button"
                        onClick={() => resetPage(key)}
                        className="text-xs text-muted-foreground underline hover:text-foreground"
                      >
                        Reset
                      </button>
                    ) : (
                      <span className="text-xs text-muted-foreground">Default</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Field matrix */}
      <div className="flex flex-col gap-1 pt-2">
        <h3 className="text-sm font-semibold text-foreground">Restricted Fields</h3>
        <p className="text-xs text-muted-foreground">
          These values are stripped from the API response as well as hidden in the app, so they are withheld rather than
          merely not drawn.
        </p>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">Field</th>
              {roles.map((role) => (
                <th key={role} className="px-3 py-2.5 text-center text-xs font-medium text-muted-foreground">
                  {ROLE_LABELS[role]}
                </th>
              ))}
              <th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {fields.map((field) => {
              const allowed = fieldRoles(field);
              const title = FIELD_LABELS[field] ?? humanise(field);
              return (
                <tr key={field} className="border-b border-border last:border-0 hover:bg-muted/30">
                  <td className="px-3 py-2">
                    <div className="font-medium text-foreground">{title}</div>
                    {FIELD_HINTS[field] && <div className="text-xs text-muted-foreground">{FIELD_HINTS[field]}</div>}
                  </td>
                  {roles.map((role) => {
                    const on = allowed.includes(role);
                    return (
                      <ToggleCell
                        key={role}
                        on={on}
                        label={`${on ? 'Hide' : 'Show'} ${title} for ${ROLE_LABELS[role]}`}
                        onClick={() => toggleFieldRole(field, role)}
                      />
                    );
                  })}
                  <td className="px-3 py-2 text-right">
                    {value.fields?.[field] ? (
                      <button
                        type="button"
                        onClick={() => resetField(field)}
                        className="text-xs text-muted-foreground underline hover:text-foreground"
                      >
                        Reset
                      </button>
                    ) : (
                      <span className="text-xs text-muted-foreground">Default</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit-section matrix */}
      {editSections.length > 0 && (
        <>
          <div className="flex flex-col gap-1 pt-2">
            <h3 className="text-sm font-semibold text-foreground">Edit Sections</h3>
            <p className="text-xs text-muted-foreground">
              Which roles can edit each section of a product's details in the mobile app (Find Product → Details). The
              server enforces this too, so a role without access cannot save the section even outside the app. Every
              edit is recorded in the product's Audit Log as Mobile, with the user's name and role.
            </p>
          </div>

          <div className="overflow-x-auto rounded-lg border border-border bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">Section</th>
                  {roles.map((role) => (
                    <th key={role} className="px-3 py-2.5 text-center text-xs font-medium text-muted-foreground">
                      {ROLE_LABELS[role]}
                    </th>
                  ))}
                  <th className="px-3 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {editSections.map(({ key, defaultRoles }) => {
                  const allowed = sectionRoles(key, defaultRoles);
                  const title = EDIT_SECTION_LABELS[key] ?? humanise(key);
                  return (
                    <tr key={key} className="border-b border-border last:border-0 hover:bg-muted/30">
                      <td className="px-3 py-2">
                        <div className="font-medium text-foreground">{title}</div>
                        {EDIT_SECTION_HINTS[key] && (
                          <div className="text-xs text-muted-foreground">{EDIT_SECTION_HINTS[key]}</div>
                        )}
                      </td>
                      {roles.map((role) => {
                        const on = allowed.includes(role);
                        return (
                          <ToggleCell
                            key={role}
                            on={on}
                            label={`${on ? 'Block' : 'Allow'} editing ${title} for ${ROLE_LABELS[role]}`}
                            onClick={() => toggleSectionRole(key, defaultRoles, role)}
                          />
                        );
                      })}
                      <td className="px-3 py-2 text-right">
                        {value.edit?.[key] ? (
                          <button
                            type="button"
                            onClick={() => resetSection(key)}
                            className="text-xs text-muted-foreground underline hover:text-foreground"
                          >
                            Reset
                          </button>
                        ) : (
                          <span className="text-xs text-muted-foreground">Default</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Save bar */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={!dirty || update.isPending}
          className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          <Save className="h-3.5 w-3.5" />
          {update.isPending ? 'Saving...' : 'Save changes'}
        </button>
        {dirty && (
          <button
            type="button"
            onClick={() => setDraft(null)}
            className="h-9 rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
          >
            Cancel
          </button>
        )}
        <span className="text-xs text-muted-foreground">
          Applies the next time the app refreshes permissions — on login, or when switching store.
        </span>
      </div>
    </div>
  );
}
